import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "./Modal";
import { Button } from "./ui/button";

interface RenderProps<T> {
  filters: T;
  onChange: (newFilters: T) => void;
}

interface FilterModalProps<T> {
  initialFilters: T;
  /** The empty/default state to apply when "Clear All" is clicked. */
  defaultFilters?: T;
  renderFilters: (props: RenderProps<T>) => React.ReactNode;
  onApply: (filters: T) => void;
  onClose: () => void;
  isOpen: boolean;
}

const FiltersModal = <T extends object>({
  isOpen,
  initialFilters,
  defaultFilters,
  renderFilters,
  onApply,
  onClose,
}: FilterModalProps<T>) => {
  const { t } = useTranslation("common");
  const [tempFilters, setTempFilters] = useState(initialFilters);
  const emptyFilters = defaultFilters ?? initialFilters;

  // Re-sync internal state whenever the modal opens so stale temp values
  // (e.g. from a previous session or an external reset) are discarded.
  useEffect(() => {
    if (isOpen) {
      setTempFilters(initialFilters);
    }
  }, [isOpen]);

  const handleApply = () => {
    onApply(tempFilters);
    onClose();
  };

  const handleClearAll = () => {
    setTempFilters(emptyFilters);
    onApply(emptyFilters);
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onCloseClick={onClose}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      size="lg"
      dialogData={{
        title: t("common:ui_kit.filter_by"),
        body: renderFilters({ filters: tempFilters, onChange: setTempFilters }),
        footer: (
          <div className="flex gap-2 justify-end pt-4 border-t">
            <Button
              variant="outline"
              className="text-gray-400 hover:text-gray-500"
              onClick={handleClearAll}
            >
              {t("common:buttons.clear_all")}
            </Button>
            <Button variant="primary" onClick={handleApply}>
              {t("common:buttons.apply")}
            </Button>
          </div>
        ),
      }}
    />
  );
};

export default FiltersModal;
